/**
 * The platform console's Knowledge candidates (L3), the pure half: labels, what a candidate's status allows, and the
 * decisions' refusals in plain words. The views are knowledge-candidates-panel.tsx.
 */
import { errorCodeOf, errorDetailOf, isNetworkError, isUnauthorized, statusOf } from "@/lib/api/error";
import type {
  KnowledgeCandidate,
  KnowledgeCandidateDecision,
  KnowledgeCandidateKind,
  KnowledgeCandidateStatus,
} from "@/lib/api/knowledge-candidates";

export const KIND_LABELS: Record<KnowledgeCandidateKind, string> = {
  alias: "Word for a known defect or parameter",
  taxonomy_entry: "Defect the taxonomy lacks",
  catalog_parameter: "Parameter the catalog lacks",
  recording_text: "How a foundry records it",
  playbook_condition: "Playbook condition",
  hypothesis_verdict: "Hypothesis verdict (count)",
  recommendation_effect: "Recommendation effect (count)",
};

export const KINDS = Object.keys(KIND_LABELS) as KnowledgeCandidateKind[];

export const STATUS_LABELS: Record<KnowledgeCandidateStatus, string> = {
  open: "Open",
  accepted: "Accepted: PR to open",
  rejected: "Rejected",
  landed: "Landed",
};

export const STATUSES = Object.keys(STATUS_LABELS) as KnowledgeCandidateStatus[];

export type StatusTone = "info" | "warning" | "secondary" | "success";

export const STATUS_TONES: Record<KnowledgeCandidateStatus, StatusTone> = {
  open: "info",
  accepted: "warning",
  rejected: "secondary",
  landed: "success",
};

/** A count is a statistic an org contributes only with its consent to counts. Every kind, content included, shapes the
 *  pack from 3 plants. */
export function isCountKind(kind: KnowledgeCandidateKind): boolean {
  return kind === "hypothesis_verdict" || kind === "recommendation_effect";
}

/** The plants behind a candidate, in words: "2 plants", and below its minimum "2 of the 3 plants it needs". */
export function plantsText(c: Pick<KnowledgeCandidate, "kind" | "plants" | "min_plants">): string {
  const n = `${c.plants} ${c.plants === 1 ? "plant" : "plants"}`;
  return c.plants >= c.min_plants ? n : `${c.plants} of the ${c.min_plants} plants it needs`;
}

/** The candidates a page of the list holds, in words: "3 candidates" when one page holds them all, else "51 to 100 of
 *  130 candidates" (offset from 0, count the page's items, total every match). */
export function pageText(offset: number, count: number, total: number): string {
  const noun = total === 1 ? "candidate" : "candidates";
  if (count >= total) return `${total} ${noun}`;
  return `${offset + 1} to ${offset + count} of ${total} ${noun}`;
}

/** A verdict as the console says it: contradicted is a hypothesis the plants' data found against (significant, the other
 *  way), not borne out one tested with adequate power that the data do not bear out. */
export function verdictWords(verdict: string): string {
  switch (verdict) {
    case "contradicted":
      return "found against";
    case "not_borne_out":
      return "not borne out";
  }
  return verdict.replace(/_/g, " ");
}

const DECISION_WORDS: Record<KnowledgeCandidateDecision["action"], string> = {
  accept: "Accepted",
  reject: "Rejected",
  landed: "Marked landed",
  reopen: "Opened again",
};

/** One line of a candidate's decision history: "Rejected by x on 2026-10-01: the note (the PR)". The system's own entry
 *  (a withdrawal left an accepted candidate below its minimum of plants) has no one to name: "Opened again on ...". */
export function decisionLine(d: KnowledgeCandidateDecision): string {
  const by = d.action === "reopen" ? "" : ` by ${d.by || "someone"}`;
  const words = `${DECISION_WORDS[d.action] ?? d.action}${by} on ${dayOf(d.at)}`;
  const note = d.note ? `: ${d.note}` : "";
  const pr = d.pr_url ? ` (${d.pr_url})` : "";
  return `${words}${note}${pr}`;
}

/** The plant day of a timestamp (its UTC date), as the console prints it. */
export function dayOf(iso: string | undefined): string {
  return iso && iso.length >= 10 ? iso.slice(0, 10) : "";
}

export interface CandidateActions {
  accept: boolean;
  reject: boolean;
  land: boolean;
  /** Why accepting is not offered, when its status would allow it; "" otherwise. */
  acceptBlocked: string;
}

/** What a candidate's status and plants allow: accept (open or rejected, and shaping the pack), reject (open or
 *  accepted), landed (accepted). The server checks each again. */
export function actionsFor(c: Pick<KnowledgeCandidate, "status" | "shapes_pack" | "shaping">): CandidateActions {
  const acceptable = c.status === "open" || c.status === "rejected";
  return {
    accept: acceptable && c.shapes_pack,
    reject: c.status === "open" || c.status === "accepted",
    land: c.status === "accepted",
    acceptBlocked: acceptable && !c.shapes_pack ? c.shaping : "",
  };
}

/** A PR link a landed candidate may carry: an https address with a host. */
export function validPrUrl(s: string): boolean {
  try {
    const u = new URL(s.trim());
    return u.protocol === "https:" && u.host !== "" && u.username === "" && s.trim().length <= 500;
  } catch {
    return false;
  }
}

/** The statement's fields as label and value lines, in a stable order (the keys sorted). */
export function statementLines(statement: Record<string, string>): { key: string; value: string }[] {
  return Object.keys(statement)
    .sort()
    .filter((k) => statement[k] !== "")
    .map((k) => ({ key: k.replace(/_/g, " "), value: statement[k] }));
}

/** Why a decision was refused, in plain words. */
export function decisionErrorText(err: unknown): string {
  const code = errorCodeOf(err);
  const detail = errorDetailOf(err);
  switch (code) {
    case "below_plant_threshold":
      return detail || "A candidate needs at least 3 plants before it shapes the pack.";
    case "invalid_transition":
      return detail || "The candidate's status has moved on: reload it.";
    case "names_an_org":
      return (
        detail ||
        "The word now carries a name on the platform (an org's name, or a name of one of its contributors): reject it."
      );
    case "note_required":
      return "A rejection says why: write the reason.";
    case "invalid_pr_url":
      return "The PR link is its https address.";
    case "not_a_plant":
      return "Only an org with real plant data shares knowledge: a demo or a rehearsal org stands for no plant.";
    case "candidate_not_found":
      return "The candidate is gone: reload the list.";
    case "not_platform_admin":
      return "Only a platform admin curates the shared knowledge.";
  }
  if (isUnauthorized(err)) return "Your session expired: sign in again.";
  if (isNetworkError(err)) return "The server could not be reached.";
  if (detail) return detail;
  const status = statusOf(err);
  return status === undefined ? "The server refused it." : `The server answered HTTP ${status}.`;
}
